import React, { useMemo, PropsWithChildren } from 'react';
import { ThemeProvider, createTheme, CssBaseline, PaletteMode, responsiveFontSizes } from '@mui/material';

export interface AppThemeProviderProps {
  mode: PaletteMode;
  density: 'compact' | 'normal' | 'comfortable';
}

/**
 * Maps our custom density value to a base spacing multiplier.
 */
function spacingFactor(density: AppThemeProviderProps['density']) {
  switch (density) {
    case 'compact': return 0.75;
    case 'comfortable': return 1.35;
    default: return 1; // normal
  }
}

export const AppThemeProvider: React.FC<PropsWithChildren<AppThemeProviderProps>> = ({ mode, density, children }) => {
  const theme = useMemo(() => {
    const dark = mode === 'dark';
    let t = createTheme({
      palette: {
        mode,
        primary: dark ? {
          main: '#1f2125',
          contrastText: '#f5f7fa'
        } : {
          main: '#2563eb', // brighter accessible blue
          contrastText: '#ffffff'
        },
        secondary: dark ? {
          main: '#0ea5e9', contrastText:'#fff'
        } : {
          main: '#0ea5e9', contrastText:'#fff'
        },
        background: dark ? {
          default: '#131518',
          paper: '#1b1e22'
        } : {
          default: '#f1f5f9',
          paper: '#ffffff'
        },
  divider: dark ? '#2b2f35' : '#d8dee6',
  // Always provide text palette keys; previously 'undefined' caused runtime errors in CssBaseline when toggling
  text: { primary: dark ? '#f0f3f6' : '#1e293b', secondary: dark ? '#9ca3af' : '#475569' }
      },
      spacing: (factor: number) => `${0.25 * spacingFactor(density) * factor}rem`,
      shape: { borderRadius: density === 'compact' ? 4 : density === 'comfortable' ? 10 : 6 },
      components: {
        MuiButton: {
          defaultProps: { variant: dark ? 'contained' : 'outlined', size: density === 'comfortable' ? 'large' : density === 'compact' ? 'small' : 'medium' },
          styleOverrides: {
            outlined: dark ? {} : {
              borderColor:'#c5d0dc',
              background:'#ffffff',
              color:'#1e293b',
              '&:hover': { background:'#f1f5f9', borderColor:'#94a3b8' }
            },
            containedPrimary: dark ? {} : {
              background:'#2563eb',
              '&:hover': { background:'#1d4ed8' }
            }
          }
        },
        MuiAppBar: {
          styleOverrides: {
            root: { backgroundImage: 'none', boxShadow: '0 0 0 1px rgba(255,255,255,0.04), 0 2px 4px -2px rgba(0,0,0,0.6)' }
          }
        },
        MuiPaper: {
          styleOverrides: {
            outlined: { backgroundImage: 'none' }
          }
        },
        MuiTextField: {
          defaultProps: { size: density === 'compact' ? 'small' : 'medium', variant: 'outlined' }
        },
        // Improve label contrast (focused labels previously used primary.main which is very dark)
        MuiInputLabel: {
          styleOverrides: {
            root: {
              color: dark ? '#9ca3af' : '#4b5563',
              fontSize: '0.78rem',
              '&.Mui-focused': { color: dark ? '#7dd3fc' : '#1d4f91' },
              '&.Mui-disabled': { opacity: 0.55 },
              // Raise shrunken outlined labels so border line doesn't cut through text
              '&.MuiInputLabel-outlined.MuiInputLabel-shrink': {
                transform: 'translate(14px, -18px) scale(0.75)',
                lineHeight: 1.1,
                padding: 0,
                background: 'transparent'
              }
            }
          }
        },
        MuiFormLabel: {
          styleOverrides: {
            root: {
              color: dark ? '#9ca3af' : '#4b5563',
              fontSize: '0.78rem',
              '&.Mui-focused': { color: dark ? '#7dd3fc' : '#1d4f91' }
            }
          }
        },
        MuiOutlinedInput: {
          styleOverrides: {
            root: {
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: dark ? '#30363d' : '#c9ced6'
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: dark ? '#46505a' : '#9aa2ac'
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: dark ? '#7dd3fc' : '#2563eb',
                boxShadow: dark ? '0 0 0 1px #7dd3fc55' : '0 0 0 1px #2563eb55'
              },
              '& input::placeholder': {
                color: dark ? '#6b7280' : '#94a3b8',
                opacity: 1
              }
            }
          }
        },
        MuiDialog: {
          styleOverrides: { paper: { minWidth: 420, maxWidth: 720 } }
        }
      }
    });
    t = responsiveFontSizes(t);
    return t;
  }, [mode, density]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
};

export default AppThemeProvider;
